import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, Download, Trash2, X, Camera, Eye, Loader2, Paperclip } from 'lucide-react';
import { uploadAttachment, getAttachmentsByEntity, downloadAttachment, downloadThumbnail, deleteAttachment } from '../../services/api';
import { resizeImageFile } from './QuoteHelpers';

export function AttachmentsModal({ isOpen, onClose, descriptionId, entityType = 'quote_description' }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: attachments = [], refetch } = useQuery({
    queryKey: ['attachments', entityType, descriptionId],
    queryFn: () => getAttachmentsByEntity(entityType, descriptionId).then(r => r.data),
    enabled: isOpen && !!descriptionId
  });

  useEffect(() => {
    if (!attachments.length) return;
    attachments.forEach(att => {
      if (att.is_image && !thumbnailUrls[att.id]) {
        downloadThumbnail(att.id).then(res => {
          const url = URL.createObjectURL(res.data);
          setThumbnailUrls(prev => ({ ...prev, [att.id]: url }));
        }).catch(() => {});
      }
    });
  }, [attachments]);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbnailUrls).forEach(url => URL.revokeObjectURL(url));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  const handlePreview = async (att) => {
    if (!att.is_image) return;
    setPreviewLoading(true);
    try {
      const res = await downloadAttachment(att.id);
      // Validate the response is actually an image (not an HTML error/redirect page)
      const blob = res.data;
      if (blob.type && !blob.type.startsWith('image/')) {
        toast.error('Failed to load preview: invalid image data');
        setPreviewLoading(false);
        return;
      }
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch { toast.error('Failed to load preview'); }
    setPreviewLoading(false);
  };

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList);
    if (!files.length) return;
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      const origName = file.name;
      setUploadProgress(`Processing ${i + 1} of ${files.length}: ${origName}`);
      // Determine if file is an image by extension or type
      const ext = origName.split('.').pop().toLowerCase();
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'heic', 'heif'];
      const isImage = file.type.startsWith('image/') || imageExts.includes(ext);
      const isHeic = ext === 'heic' || ext === 'heif' || file.type === 'image/heic' || file.type === 'image/heif';

      // Resize standard images client-side before upload (skip HEIC - backend handles conversion)
      if (isImage && !isHeic) {
        try {
          file = await resizeImageFile(file, 1200, 0.7);
        } catch (err) {
          console.error('Image resize failed, uploading original:', err);
        }
      }

      setUploadProgress(`Uploading ${i + 1} of ${files.length}: ${origName}`);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('entity_id', descriptionId);
      try {
        await uploadAttachment(formData);
        successCount++;
      } catch (err) {
        failCount++;
        const detail = err.response?.data?.detail || err.message || 'Upload error';
        toast.error(`Failed to upload ${origName}: ${detail}`);
      }
    }
    setUploading(false);
    setUploadProgress('');
    refetch();
    if (successCount > 0) toast.success(`${successCount} file(s) uploaded`);
    if (failCount > 0 && successCount === 0) toast.error('All uploads failed. Try smaller files or a different format.');
  };

  const handleFileChange = async (e) => {
    await handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Drag-and-drop support: drop files from email, file explorer, etc. directly onto the modal.
  // Uses a ref counter because dragenter/dragleave fire on every child element as the cursor moves.
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) {
      dragCounterRef.current += 1;
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length) {
      await handleFiles(droppedFiles);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, descriptionId]);

  // Paste support: paste images copied from email clients (e.g. Outlook) directly into the modal.
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) pastedFiles.push(f);
      }
    }
    if (pastedFiles.length) {
      e.preventDefault();
      await handleFiles(pastedFiles);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, descriptionId]);

  const handleDelete = async (id) => {
    try {
      await deleteAttachment(id);
      refetch();
      toast.success('Attachment removed');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = async (att) => {
    try {
      const res = await downloadAttachment(att.id);
      const blob = res.data;
      if (blob.type && !blob.type.startsWith('image/') && !blob.type.startsWith('application/')) {
        toast.error('Download failed: invalid response from server');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Image Preview Overlay */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-8" data-testid="image-preview-overlay" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <button className="absolute top-4 right-4 text-white hover:text-slate-300" onClick={(e) => { e.stopPropagation(); URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
            <X size={28} />
          </button>
          <img
            src={previewUrl}
            alt="Preview"
            className="max-w-full max-h-full object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); toast.error('Failed to load image preview'); }}
          />
        </div>
      )}
      {previewLoading && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center">
          <Loader2 size={40} className="animate-spin text-white" />
        </div>
      )}

      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        data-testid="attachments-modal"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={`bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col relative transition-colors ${isDragging ? 'ring-4 ring-primary ring-offset-2' : ''}`}
          onPaste={handlePaste}
          tabIndex={-1}
          data-testid="attachments-dropzone"
        >
          {isDragging && (
            <div className="absolute inset-0 bg-primary/10 backdrop-blur-sm rounded-lg z-10 flex items-center justify-center pointer-events-none" data-testid="dropzone-overlay">
              <div className="bg-white px-6 py-4 rounded-lg shadow-lg border-2 border-dashed border-primary flex flex-col items-center gap-2">
                <Upload size={32} className="text-primary" />
                <p className="text-primary font-semibold">Drop files to upload</p>
                <p className="text-xs text-slate-500">Images, PDFs, Word, Excel supported</p>
              </div>
            </div>
          )}
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold">Attachments</h2>
            <button onClick={onClose} data-testid="attachments-close"><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {uploading && (
              <div className="p-3 bg-blue-50 rounded text-sm text-blue-700 flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                {uploadProgress}
              </div>
            )}
            {attachments.length === 0 && !uploading ? (
              <div className="text-center py-8 text-slate-400">
                <Upload size={28} className="mx-auto mb-2 opacity-60" />
                <p className="text-sm">No attachments yet</p>
                <p className="text-xs mt-1">Drag &amp; drop files here, paste from clipboard, or click <span className="font-medium">Add File</span></p>
              </div>
            ) : (
              attachments.map((att) => (
                <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded border" data-testid={`attachment-${att.id}`}>
                  <div
                    className={`w-14 h-14 bg-slate-200 rounded overflow-hidden flex items-center justify-center flex-shrink-0 ${att.is_image ? 'cursor-pointer hover:ring-2 hover:ring-primary' : ''}`}
                    onClick={() => att.is_image && handlePreview(att)}
                    title={att.is_image ? 'Click to preview' : ''}
                  >
                    {att.is_image && thumbnailUrls[att.id] ? (
                      <img src={thumbnailUrls[att.id]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Paperclip size={20} className="text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{att.filename}</p>
                    <p className="text-xs text-slate-400">
                      {formatSize(att.file_size)}
                      {att.is_image && att.width && ` - ${att.width}x${att.height}`}
                      {att.optimized_size && ` (opt: ${formatSize(att.optimized_size)})`}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {att.is_image && (
                      <button onClick={() => handlePreview(att)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Preview" data-testid={`preview-attachment-${att.id}`}>
                        <Eye size={14} />
                      </button>
                    )}
                    <button onClick={() => handleDownload(att)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Download">
                      <Download size={14} />
                    </button>
                    <button onClick={() => handleDelete(att.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove" data-testid={`delete-attachment-${att.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="p-4 border-t flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-2"
                data-testid="upload-attachment-btn"
              >
                <Upload size={16} />
                Add File
              </button>
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="px-4 py-2 text-sm bg-[#178c98] text-white rounded hover:bg-[#136e78] flex items-center gap-2"
                data-testid="take-photo-btn"
              >
                <Camera size={16} />
                Take Photo
              </button>
              <input type="file" ref={fileInputRef} className="hidden" multiple accept="image/*,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileChange} />
              <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={handleFileChange} data-testid="camera-input" />
            </div>
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Close</button>
          </div>
        </div>
      </div>
    </>
  );
}

// Add/Remove Items Modal (Cost Builder)
